import { computed, onMounted, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from './useI18n'

/**
 * 组合式函数：管理页面head标签（title, meta description等）
 */
export function useHead() {
  const route = useRoute()
  const { t, language } = useI18n()

  // 路由路径到meta key的映射
  const routeToMetaKeyMap: Record<string, string> = {
    '/ai-advisor': 'aiAdvisor',
    '/par-analyzer': 'parAnalyzer',
    '/water-change': 'waterManagement',
    '/elements': 'elements',
    '/nutrients': 'nutrients',
    '/feeding': 'feeding',
    '/tank-log': 'tankLog',
    '/quarantine': 'quarantine'
  }

  // 获取当前页面的meta描述
  const metaDescription = computed(() => {
    const metaKey = routeToMetaKeyMap[route.path] || 'default'
    try {
      const description = t(`common.meta.${metaKey}`).value
      console.log(`[useHead] meta description for ${route.path} (key: ${metaKey}):`, description)
      return description || t('common.meta.default').value
    } catch (error) {
      console.error(`[useHead] Error getting meta description for ${metaKey}:`, error)
      return t('common.meta.default').value
    }
  })

  // 获取当前页面的标题
  const pageTitle = computed(() => {
    const tabKey = routeToMetaKeyMap[route.path]
    if (tabKey) {
      const tabName = t(`common.tabs.${tabKey}`).value
      const appTitle = t('common.appTitle').value
      const title = `${tabName} - ${appTitle}`
      console.log(`[useHead] page title for ${route.path}:`, title)
      return title
    }
    const defaultTitle = t('common.appTitle').value
    console.log(`[useHead] default page title:`, defaultTitle)
    return defaultTitle
  })

  // 更新head标签
  const updateHead = () => {
    try {
      console.log(`[useHead] updateHead called for path: ${route.path}, language: ${language.value}`)
      
      // 确保翻译数据已加载
      if (!metaDescription.value || !pageTitle.value) {
        console.log(`[useHead] Translation data not ready, retrying...`)
        // 如果翻译数据未加载，延迟重试
        setTimeout(updateHead, 100)
        return
      }

      console.log(`[useHead] Updating title to: ${pageTitle.value}`)
      console.log(`[useHead] Updating meta description to: ${metaDescription.value}`)

      // 更新title
      document.title = pageTitle.value
      console.log(`[useHead] Document title set to: ${document.title}`)

      // 更新或创建meta description标签
      let metaDescriptionTag = document.querySelector('meta[name="description"]')
      console.log(`[useHead] Query for meta[name="description"] result:`, metaDescriptionTag)
      
      if (!metaDescriptionTag) {
        console.log(`[useHead] Creating new meta description tag`)
        metaDescriptionTag = document.createElement('meta')
        metaDescriptionTag.setAttribute('name', 'description')
        document.head.appendChild(metaDescriptionTag)
      } else {
        console.log(`[useHead] Found existing meta description tag, current content:`, metaDescriptionTag.getAttribute('content'))
      }
      
      const newContent = metaDescription.value
      metaDescriptionTag.setAttribute('content', newContent)
      console.log(`[useHead] Meta description content set to: ${newContent}`)
      
      // 验证更新是否成功
      const verifiedContent = document.querySelector('meta[name="description"]')?.getAttribute('content')
      console.log(`[useHead] Verified meta description content: ${verifiedContent}`)

      // 更新或创建og:description标签
      let ogDescriptionTag = document.querySelector('meta[property="og:description"]')
      if (!ogDescriptionTag) {
        ogDescriptionTag = document.createElement('meta')
        ogDescriptionTag.setAttribute('property', 'og:description')
        document.head.appendChild(ogDescriptionTag)
      }
      ogDescriptionTag.setAttribute('content', metaDescription.value)

      // 更新或创建og:title标签
      let ogTitleTag = document.querySelector('meta[property="og:title"]')
      if (!ogTitleTag) {
        ogTitleTag = document.createElement('meta')
        ogTitleTag.setAttribute('property', 'og:title')
        document.head.appendChild(ogTitleTag)
      }
      ogTitleTag.setAttribute('content', pageTitle.value)

      // 更新或创建og:url标签
      let ogUrlTag = document.querySelector('meta[property="og:url"]')
      if (!ogUrlTag) {
        ogUrlTag = document.createElement('meta')
        ogUrlTag.setAttribute('property', 'og:url')
        document.head.appendChild(ogUrlTag)
      }
      ogUrlTag.setAttribute('content', window.location.href)

      // 更新html lang属性
      const htmlElement = document.documentElement
      const langMap: Record<string, string> = {
        'zh-CN': 'zh-CN',
        'en-US': 'en',
        'de-DE': 'de'
      }
      const lang = langMap[language.value] || 'en'
      htmlElement.setAttribute('lang', lang)
      console.log(`[useHead] Updated html lang to: ${lang}`)
    } catch (error) {
      console.error('[useHead] Error updating head tags:', error)
    }
  }

  // 使用watchEffect自动跟踪所有响应式依赖
  watchEffect(() => {
    console.log(`[useHead] watchEffect triggered, path: ${route.path}, language: ${language.value}`)
    // 访问计算属性以确保它们被跟踪为依赖
    const desc = metaDescription.value
    const title = pageTitle.value
    
    if (desc && title) {
      console.log(`[useHead] Data ready, scheduling updateHead`)
      // 延迟更新以确保DOM已准备好
      setTimeout(updateHead, 50)
    } else {
      console.log(`[useHead] Data not ready yet, retrying...`)
      setTimeout(updateHead, 100)
    }
  })

  // 初始更新
  onMounted(() => {
    console.log(`[useHead] Component mounted, calling updateHead`)
    setTimeout(updateHead, 100)
  })

  return {
    metaDescription,
    pageTitle,
    updateHead
  }
}